
import hasCurrentFirmUuid from '../mixins/hasCurrentFirmUuid';
import casesAPI from '../api/cases';

export default {
  name: 'ClientCaseSelect',

  mixins: [hasCurrentFirmUuid],

  props: {
    client_uuid: {
      type: String | null,
      required: false,
      default: null,
    },
    value: {
      type: String | Object | Array,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentCaseUuid: {
      type: String,
      required: false,
      default: null,
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: false,
    },
    // When supplied, the selector scopes to every case belonging to these accounts
    // (a comma-joined `filter[account_uuids]`) instead of the single-client endpoint.
    // This is the only path that supports more than one account, e.g. joint applicants.
    account_uuids: {
      type: Array | String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      selectedList: [],
      caseList: [],
      loading: true,
    };
  },

  computed: {
    hasAccountScope() {
      return Array.isArray(this.account_uuids) ? this.account_uuids.length > 0 : !!this.account_uuids;
    },
  },

  created() {
    this.grabCases();
  },

  methods: {
    async grabCases() {
      this.loading = true;

      try {
        let cases = [];

        if (this.hasAccountScope) {
          // getAllCases pages through the whole scoped result, so the "showing all"
          // helper text is honest even for clients with many cases.
          cases = await casesAPI.getAllCases(this.buildAccountScopedParams());
        } else if (this.client_uuid != null) {
          cases = await casesAPI.getAllClientCases(this.client_uuid);
        }

        // A pre-selected case can sit outside the scoped list (e.g. seeded from a case
        // whose participants were never chosen as applicants). Pull it in by uuid so the
        // selection is never silently dropped.
        cases = await this.mergeSelectedCases(cases);

        this.caseList = this.currentCaseUuid ? cases.filter((item) => item.uuid !== this.currentCaseUuid) : cases;

        this.setSelectedFromValue();
      } finally {
        this.loading = false;
      }
    },

    buildAccountScopedParams() {
      return {
        'filter[account_uuids]': Array.isArray(this.account_uuids) ? this.account_uuids.join(',') : this.account_uuids,
      };
    },

    async mergeSelectedCases(cases) {
      const missing = this.selectedValueUuids().filter((uuid) => !cases.some((item) => item.uuid === uuid));

      if (!missing.length) {
        return cases;
      }

      try {
        const rsp = await casesAPI.getCases({
          params: { 'filter[uuid]': missing.join(','), per_page: missing.length },
        });

        return [...rsp.data.data, ...cases];
      } catch (err) {
        return cases;
      }
    },

    selectedValueUuids() {
      if (Array.isArray(this.value)) {
        return this.value.map((item) => (item && typeof item === 'object' ? item.uuid : item)).filter(Boolean);
      }

      if (this.value && typeof this.value === 'object') {
        return this.value.uuid ? [this.value.uuid] : [];
      }

      return this.value ? [this.value] : [];
    },

    setSelectedFromValue() {
      if (!this.value) {
        return;
      }

      if (Array.isArray(this.value)) {
        this.selectedList = this.caseList.filter((item) => this.value.includes(item.uuid));
      } else if (typeof this.value === 'string') {
        this.selectedList = this.caseList.find((item) => item.uuid === this.value) || null;
      } else if (typeof this.value === 'object') {
        this.selectedList = this.caseList.find((item) => item.uuid === this.value.uuid) || null;
      }
    },

    handleSearchChange() {
      // The account-scoped list is loaded in full and filtered client-side, so typing
      // must not refetch. The single-client path keeps its previous search behaviour.
      if (this.hasAccountScope) {
        return;
      }

      this.grabCases();
    },
  },

  watch: {
    client_uuid() {
      this.grabCases();
    },

    account_uuids() {
      this.grabCases();
    },

    selectedList(new_val) {
      if (new_val === null) {
        this.$emit('input', null);
        return;
      }
      if (!Array.isArray(new_val)) {
        if (new_val && new_val.uuid !== null) {
          this.$emit('input', new_val.uuid);
        }
        return;
      }

      let reduced = new_val.filter((item) => item && item.uuid !== null).map((item) => item.uuid);

      this.$emit('input', reduced);
    },
  },
};
